/** The tour's launch readiness (plan §1 "Launch band"): pure rules over what the band and
 *  the Launch & mapping panel's checks list show, so both say the same thing.
 *
 *  Every rule is a finding to look at (the band's amber "!" node), never a block on
 *  saving — a tour can legitimately run with any of them:
 *
 *  - no primary `custom_id` on an import-bound tour;
 *  - a parked alias that nothing else matches for its system: no other matching alias of
 *    that system (an alias without a system matches every system) and no primary ID —
 *    booking sync's tour map takes the primary `custom_id` for every system
 *    (`BaseIntegration.get_tour_map`: no integration filter; it needs the tour not
 *    deleted), and the importer matches it literally;
 *  - an alias naming a booking system, or an account of one, that the operator has not
 *    configured (waits for the operator's list — `restechs: null`);
 *  - the tour's own launch policy differs from the operator default;
 *  - the tour has a policy of its own that is not enforced — its OWNER (the operator, its
 *    franchise parent, or the platform for a global policy) does not enforce launch
 *    policies, so it holds nothing back;
 *  - the latest import run failed or finished with errors (the Import node);
 *  - a recurring tour without departures (the checks list only — no band node holds
 *    departures; it follows the draft's departures and links to them).
 *
 *  The Guest app node has no rule: the plan states none for it (self-registration, chat,
 *  My Trips are its datum line only), so it keeps saying "No checks".
 *
 *  Only Mapping, Import and Launch policy have rules (`RULED_NODES`); the other nodes
 *  say "No checks" rather than claim "Ready".
 *
 *  The plan's seventh rule — "check-in category missing when the operator requires
 *  check-in" — is not here: no operator setting requires check-in (the operator config
 *  schema has check-in windows and QR fields only; the dashboard's check-in section just
 *  says "No check-in categories selected").
 *
 *  The input is a snapshot: the stored tour in view mode, the stored tour with the draft
 *  laid over it in edit mode (`readinessInputOf`). */
import type { TourDetail } from '@/api/tours';

/** An import-bound tour without a primary ID: imports cannot match it (the strip and the
 *  identifiers table say so in amber). */
export const MISSING_PRIMARY = 'No primary ID — imports can’t match this tour';

/** The launch band's six nodes, in key order (`1`–`6`). */
export const READINESS_NODES = ['mapping', 'import', 'policy', 'access', 'tag-categories', 'guest-app'] as const;
export type ReadinessNode = (typeof READINESS_NODES)[number];
/** The nodes some rule looks at — the others have nothing to be "Ready" about. */
export const RULED_NODES: ReadonlySet<ReadinessNode> = new Set(['mapping', 'import', 'policy']);

/** Import_Run states the Import node calls out (IMPORT_RUN_STATES). */
const RUN_FAILURE = 6;
const RUN_PARTIAL = 5;

export interface ReadinessAlias {
  /** The row's record id (negative: a row the draft creates). */
  id: number;
  customId: string | null;
  matching: boolean;
  integration: string | null;
  account: string | null;
}

export interface ReadinessInput {
  tourId: number;
  customId: string | null;
  /** The primary ID takes part in matching: set, on a tour that is not deleted. */
  primaryMatches: boolean;
  importBound: boolean;
  /** The latest import run (newest first), if any. */
  lastRun: { state: number; source: string | null } | null;
  aliases: ReadinessAlias[];
  /** The booking systems and accounts the operator has configured; null = not known yet. */
  restechs: ReadonlyArray<{ code: string; account: string | null }> | null;
  /** The tour's own launch policy (null = it inherits the operator default), and whether
   *  its owner enforces launch policies (null = not known yet). */
  ownPolicy: { id: number; name: string | null; enforced: boolean | null } | null;
  operatorDefaultPolicy: { id: number; name: string | null } | null;
  isBespoke: boolean;
  departureCount: number;
}

export interface ReadinessCheck {
  /** Stable per finding (`parked_alone:13`). */
  key: string;
  code:
    | 'no_primary_id'
    | 'parked_alone'
    | 'unknown_system'
    | 'unknown_account'
    | 'policy_not_default'
    | 'policies_off_with_own'
    | 'import_failed'
    | 'no_departures';
  /** The band node it marks; null = the checks list only. */
  node: ReadinessNode | null;
  title: string;
  detail: string;
  /** The field the check is about (focused from the checks list). */
  target?: { rec: string; path: string };
}

/** How an alias is named in a sentence: its value, else the new row's placeholder. */
export function aliasName(a: Pick<ReadinessAlias, 'id' | 'customId'>): string {
  const v = a.customId?.trim();
  if (v) return v;
  return a.id < 0 ? `NEW-${-a.id}` : `#${a.id}`;
}

/** Two aliases can stand in for each other when either has no system or both name the same. */
function sameSystem(a: ReadinessAlias, b: ReadinessAlias): boolean {
  return !a.integration || !b.integration || a.integration === b.integration;
}

const policyName = (p: { id: number; name: string | null }) => p.name || `#${p.id}`;

export function readinessChecks(input: ReadinessInput): ReadinessCheck[] {
  const out: ReadinessCheck[] = [];
  const tourRec = `tour:${input.tourId}`;
  const aliasRec = (a: ReadinessAlias) => `alias:${a.id}`;

  if (input.importBound && !input.customId?.trim()) {
    out.push({
      key: 'no_primary_id',
      code: 'no_primary_id',
      node: 'mapping',
      title: 'No primary ID',
      detail: 'Imports feed this tour, but it has no primary external ID to match products and bookings by.',
      target: { rec: tourRec, path: 'custom_id' },
    });
  }

  for (const a of input.aliases) {
    if (a.matching) continue;
    // The primary ID matches for every system.
    const covered = input.primaryMatches || input.aliases.some((b) => b !== a && b.matching && sameSystem(a, b));
    if (covered) continue;
    const system = a.integration ? ` for ${a.integration}` : '';
    out.push({
      key: `parked_alone:${a.id}`,
      code: 'parked_alone',
      node: 'mapping',
      title: `${aliasName(a)} is parked`,
      detail: `No primary ID and no other alias matches${system} — bookings that only carry this ID find no tour.`,
      target: { rec: aliasRec(a), path: 'alias.use_for_matching' },
    });
  }

  if (input.restechs) {
    const codes = new Set(input.restechs.map((r) => r.code));
    for (const a of input.aliases) {
      if (!a.integration) continue;
      if (!codes.has(a.integration)) {
        out.push({
          key: `unknown_system:${a.id}`,
          code: 'unknown_system',
          node: 'mapping',
          title: `${aliasName(a)}: unknown system “${a.integration}”`,
          detail: 'The operator has no booking system with this name configured.',
          target: { rec: aliasRec(a), path: 'alias.integration' },
        });
      } else if (a.account && !input.restechs.some((r) => r.code === a.integration && r.account === a.account)) {
        out.push({
          key: `unknown_account:${a.id}`,
          code: 'unknown_account',
          node: 'mapping',
          title: `${aliasName(a)}: unknown account “${a.account}”`,
          detail: `${a.integration} has no account with this name for this operator.`,
          target: { rec: aliasRec(a), path: 'alias.integration' },
        });
      }
    }
  }

  const own = input.ownPolicy;
  if (own && own.id !== input.operatorDefaultPolicy?.id) {
    const def = input.operatorDefaultPolicy;
    out.push({
      key: 'policy_not_default',
      code: 'policy_not_default',
      node: 'policy',
      title: 'Not the operator default',
      detail: def
        ? `This tour launches with ${policyName(own)}; the operator default is ${policyName(def)}.`
        : `This tour launches with ${policyName(own)}; the operator has no default policy.`,
      target: { rec: tourRec, path: 'launch_policy' },
    });
  }
  if (own && own.enforced === false) {
    out.push({
      key: 'policies_off_with_own',
      code: 'policies_off_with_own',
      node: 'policy',
      title: 'Launch policy not enforced',
      detail: `${policyName(own)} belongs to a tenant that does not enforce launch policies, so it holds nothing back.`,
      target: { rec: tourRec, path: 'launch_policy' },
    });
  }

  const run = input.lastRun;
  if (run && (run.state === RUN_FAILURE || run.state === RUN_PARTIAL)) {
    const from = run.source ? ` from ${run.source}` : '';
    out.push({
      key: 'import_failed',
      code: 'import_failed',
      node: 'import',
      title: run.state === RUN_FAILURE ? 'Last import failed' : 'Last import had errors',
      detail: `The latest import run${from} ${run.state === RUN_FAILURE ? 'failed' : 'finished with errors'} — the tour may not carry its latest data.`,
    });
  }

  if (!input.isBespoke && input.departureCount === 0) {
    out.push({
      key: 'no_departures',
      code: 'no_departures',
      node: null,
      title: 'No departures',
      detail: 'Bookings of a recurring tour land on a departure — this tour has none (any save creates one from its start time).',
      target: { rec: `tour:${input.tourId}`, path: 'departures' },
    });
  }
  return out;
}

/** The checks by node, in node order. */
export function checksByNode(checks: readonly ReadinessCheck[]): Record<ReadinessNode, ReadinessCheck[]> {
  const out = Object.fromEntries(READINESS_NODES.map((n) => [n, [] as ReadinessCheck[]])) as Record<
    ReadinessNode,
    ReadinessCheck[]
  >;
  for (const c of checks) if (c.node) out[c.node].push(c);
  return out;
}

/** The readiness input of the stored tour, with the draft's values laid over it (`valueOf`
 *  answers a path of a record: the draft's value in edit mode, the stored one in view
 *  mode; undefined = not known). */
export function readinessInputOf(
  detail: TourDetail,
  {
    aliasIds,
    valueOf,
    restechs,
    policyOf,
  }: {
    /** The alias rows in order (the membership list, draft included). */
    aliasIds: readonly number[];
    valueOf: (rec: string, path: string) => unknown;
    restechs: ReadinessInput['restechs'];
    /** A policy the draft picked: its name and whether its owner enforces launch
     *  policies (the stored one's are known). */
    policyOf?: (id: number) => { name?: string | null; enforced?: boolean | null } | undefined;
  },
): ReadinessInput {
  const tourRec = `tour:${detail.id}`;
  const text = (v: unknown) => (typeof v === 'string' ? v : null);
  const policyId = valueOf(tourRec, 'launch_policy');
  const stored = detail.launch_policy?.id === policyId;
  const picked = typeof policyId === 'number' && !stored ? policyOf?.(policyId) : undefined;
  const ownPolicy =
    typeof policyId === 'number'
      ? {
          id: policyId,
          name: (stored ? detail.launch_policy!.name : picked?.name) ?? null,
          enforced: (stored ? detail.launch_policy_enforced : picked?.enforced) ?? null,
        }
      : null;
  return {
    tourId: detail.id,
    customId: text(valueOf(tourRec, 'custom_id')),
    primaryMatches: Boolean(text(valueOf(tourRec, 'custom_id'))?.trim()) && !detail.is_deleted,
    importBound: detail.import_summary.is_import_bound,
    lastRun: detail.import_summary.runs?.[0]
      ? {
          state: detail.import_summary.runs[0].state,
          source: detail.import_summary.runs[0].sources.map((s) => s.name || s.code).join(', ') || null,
        }
      : null,
    aliases: aliasIds.map((id) => {
      const rec = `alias:${id}`;
      return {
        id,
        customId: text(valueOf(rec, 'alias.custom_id')),
        matching: valueOf(rec, 'alias.use_for_matching') !== false,
        integration: text(valueOf(rec, 'alias.integration')),
        account: text(valueOf(rec, 'alias.restech_account')),
      };
    }),
    restechs,
    ownPolicy,
    operatorDefaultPolicy: detail.operator_default_launch_policy
      ? { id: detail.operator_default_launch_policy.id, name: detail.operator_default_launch_policy.name }
      : null,
    isBespoke: detail.is_bespoke,
    // The departures the draft leaves (an id set on the tour record since P3).
    departureCount: (() => {
      const v = valueOf(tourRec, 'departures');
      return Array.isArray(v) ? v.length : (detail.departures ?? []).length;
    })(),
  };
}
